import * as React from 'react'
import { ReactNode, useState } from 'react'
import { UAParser } from 'ua-parser-js'
import { Destination, IGraphArrayPayload } from '../context/AxiosContext'
import { DriveItem, Message } from '@microsoft/microsoft-graph-types'
import { Link } from '@fluentui/react'
import { AxiosInstance } from 'axios'
import LoadableText from './LoadableText'
import { getDownloadUrl, getDriveItemDownload, resolveOpenUrl, shouldOpen } from '../helpers/documentHelper'
import { isOfficeInitialized, outlookDisplayMessageForm } from '../helpers/outlookHelper'
import useAxios from '../hooks/useAxios'
import { appInsightsReact } from '../pages/App'
import { SeverityLevel } from '@microsoft/applicationinsights-common'
import { ICustomProperties } from '@microsoft/applicationinsights-core-js'
import * as UUID from 'uuid'
import useDocumentsOpen from '../hooks/document/useDocumentsOpen'
import { DocumentsOpen } from '../context/GlobalContext'

export type StealthLinkProps<E = DriveItem | Message> = {
    id: string // Can be Message ID or DriveItem ID. Required for direct access.
    universalId?: string // The universal ID to identify duplicates. Only use by Message.

    item?: E //Either a DriveItem or a Message
    url?: string //Custom URL with which the item can be downloaded.

    destination?: Destination
    documentsOpen?: DocumentsOpen
    children?: ReactNode | string | undefined
    trackId?: string // An optional ID to track events and exceptions
}

const getId = (props: StealthLinkProps): string => props.item?.id || props.id
const getUrl = (props: StealthLinkProps<DriveItem>): string => resolveOpenUrl(props.item, props.documentsOpen) || props.url || ''
const getDestination = (props: StealthLinkProps): Destination => props.destination || Destination.Documents

const isOfficeUrl = (props: StealthLinkProps): boolean => {
    const result = getUrl(props).indexOf('ms-') === 0
    console.log('Url is an office url:', result)
    return result
}

// Safari does not always support blob downloads.
const isSafari = (): boolean => {
    const ua = new UAParser().getResult()
    console.log('Client is:', JSON.stringify(ua))
    return ['Safari', 'WebKit'].includes(ua.browser?.name)
}

const isDriveItemProps = (props: StealthLinkProps): props is StealthLinkProps<DriveItem> => {
    return getDestination(props) === Destination.Documents
}

const isMessageProps = (props: StealthLinkProps): props is StealthLinkProps<Message> => {
    return [Destination.ReplicationEmail, Destination.Email].includes(getDestination(props))
}

/**
 * https://jsfiddle.net/hielkehoeve/oa628mwd/ - direct links
 * https://jsfiddle.net/253jganx/1/ - onclick links
 * https://jsfiddle.net/u1hkgarz/ - onclick fetch/promise links
 *
 * @param blob the blob of the item
 * @param filename the item name
 */
export const fileDownload = (blob: Blob, filename: string): void => {
    const blobURL = window.URL && window.URL.createObjectURL ? window.URL.createObjectURL(blob) : window.webkitURL.createObjectURL(blob)

    const tempLink = document.createElement('a')
    tempLink.style.display = 'none'
    tempLink.href = blobURL
    tempLink.setAttribute('download', filename)

    // Safari thinks _blank anchors are pop-ups. We only want to set _blank
    // target if the browser does not support the HTML5 download attribute.
    // This allows you to download files in desktop safari if pop up blocking
    // is enabled.
    if (typeof tempLink.download === 'undefined') {
        console.log('target = _blank')
        tempLink.setAttribute('target', '_blank')
    }

    tempLink.addEventListener('click', () => {
        console.log('blobURL clicked')
    })

    document.body.appendChild(tempLink)

    tempLink.dispatchEvent(
        new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
        }),
    )

    setTimeout(function () {
        document.body.removeChild(tempLink)
        window.URL.revokeObjectURL(blobURL)
        console.log('blob removed')
    }, 5_000)
}

//When using Safari, we fall back to opening a new window. Either through Outlook or ourselves.
const checkStealthClickForSafari = (props: StealthLinkProps) => {
    if (isSafari()) {
        trackEvent('StealthClick', 'windowOpenSafari', props)
        if (isOfficeInitialized() && !isOfficeUrl(props) && Office.context.ui.openBrowserWindow) {
            console.log('Opening item using openBrowserWindow for Safari with url:', getUrl(props))
            Office.context.ui.openBrowserWindow(getUrl(props))
            return Promise.resolve()
        } else if (isOfficeInitialized()) {
            console.log('Opening item using window.open for Safari with url:', getUrl(props))
            window.open(getUrl(props), '_blank')
            return Promise.resolve()
        } else {
            console.log('Opening item using window.open for Safari with url:', getUrl(props))
            window.open(getUrl(props), isOfficeUrl(props) ? '_self' : '_blank')
            return Promise.resolve()
        }
    }
    return Promise.reject()
}

const getMessageItem = (props: StealthLinkProps<Message>, axios: AxiosInstance, destination: Destination) => {
    return axios.get<IGraphArrayPayload<Message>>('/messages/', {
        baseURL: destination,
        headers: { SuppressErrors: 'true', Prefer: 'IdType="ImmutableId"' },
        params: {
            $select: 'id',
            $filter: `internetMessageId eq '${props.universalId}'`,
        },
    })
}

const stealthClickMessageWithoutWindow = (props: StealthLinkProps<Message>, axios: AxiosInstance): Promise<void | Window> => {
    const openCallback = (result?: Office.AsyncResult<void>, error?: Error) => {
        // Safari doesn't seem to understand the status value.
        if (result && `${result.status}` === 'succeeded') {
            console.log('Opened e-mail')
            trackEvent('StealthClick', 'emailOpened', props)
            return Promise.resolve()
        } else {
            if (error) {
                console.log('Could not open e-mail:', error.message)
            }

            //Check if the user is running in Safari. Otherwise try to download the email inline.
            return checkStealthClickForSafari(props).catch(() => {
                // E-mails are downloaded straight from Exchange.
                console.log('Downloading e-mail as blob using id:', props.id)
                return axios
                    .get<Blob>(`/messages/${props.id}/$value`, { responseType: 'blob', baseURL: getDestination(props) })
                    .then(response => {
                        console.log('Serving e-mail as download:', props.id)
                        trackEvent('StealthClick', 'emailDownload', props)
                        fileDownload(response.data, `${props.id}.eml`)
                    })
                    .catch(error => {
                        console.log('Could not download e-mail as blob using id', props.id, '. error:', error)
                        trackException(error, props, {
                            reactComponentPath: 'emailDownload',
                            message: 'Could not download e-mail as blob using id',
                        })
                    })
            })
        }
    }

    // If we have an e-mail that we might open in Outlook natively.
    if (isOfficeInitialized() && props.destination === Destination.Email) {
        console.log('Opening e-mail in Outlook from private mailbox:', props.id)
        outlookDisplayMessageForm(props.id, openCallback, openCallback)
        return Promise.resolve()
    }

    if (isOfficeInitialized() && props.destination === Destination.ReplicationEmail && props.universalId) {
        console.log('Opening e-mail in Outlook from private or shared mailbox using universalId:', props.universalId)
        return getMessageItem(props, axios, Destination.Email)
            .then(result => {
                if (result.data.value?.length > 0) {
                    console.log('Opening e-mail from private mailbox in Outlook using id:', result.data.value[0].id)
                    outlookDisplayMessageForm(result.data.value[0].id, openCallback, openCallback)
                    return Promise.resolve()
                }
                console.log('E-mail not found in private mailbox:', result.status, result.statusText)
                return getMessageItem(props, axios, Destination.ReplicationEmail).then(result2 => {
                    if (result2.data.value?.length > 0) {
                        console.log('Opening e-mail from shared mailbox in Outlook using id:', result2.data.value[0].id)
                        outlookDisplayMessageForm(result2.data.value[0].id, openCallback, openCallback)
                        return Promise.resolve()
                    }
                    console.log('No response data found. error?:', result2.status, result2.statusText)
                    return openCallback(undefined)
                })
            })
            .catch(error => {
                console.log('Could not download e-mail using universalId:', props.universalId, '. error:', error)
                return openCallback(undefined)
            })
    }

    return openCallback(undefined)
}

const stealthClickDriveItem = (props: StealthLinkProps<DriveItem>, axios: AxiosInstance): Promise<void | Window> => {
    //Make sure we have at least a URL for the given id / item.
    return (props.item ? getDriveItemDownload(props.item.id, axios) : getDriveItemDownload(props.id, axios)).then(item => {
        props.item = item
        return checkStealthClickForSafari(props).catch(() => {
            if (shouldOpen(props.documentsOpen, isOfficeUrl(props))) {
                trackEvent('StealthClick', 'windowOpenOther', props)
                console.log('Opening item using window.open with url:', getUrl(props))
                return Promise.resolve().then(() => window.open(getUrl(props), '_blank'))
            } else {
                trackEvent('StealthClick', 'downloadOther', props)
                const url = getDownloadUrl(item)
                console.log('Downloading item (#', item.id, ') as blob using url:', url)
                return Promise.resolve().then(() => window.location.assign(url))
            }
        })
    })
}

//Check if we are opening a DriveItem or Email Item.
const doStealthClick = (props: StealthLinkProps, axios: AxiosInstance): Promise<void | Window> => {
    if (isDriveItemProps(props)) {
        trackEvent('StealthClick', 'stealthClickDriveItem', props)
        return stealthClickDriveItem(props, axios)
    } else if (isMessageProps(props)) {
        trackEvent('StealthClick', 'stealthClickMessageWithoutWindow', props)
        return stealthClickMessageWithoutWindow(props, axios)
    }
    return Promise.reject('Item is not of the correct type to be downloaded')
}

export const stealthClick = (props: StealthLinkProps, axios: AxiosInstance): Promise<void | Window> => {
    const trackedProps: StealthLinkProps = { ...props, trackId: UUID.v4() }
    trackEvent('StealthClick', 'stealthClick', trackedProps)

    if (!getId(trackedProps)) {
        console.log('No ID found')
        return Promise.reject()
    }

    return doStealthClick(trackedProps, axios).catch(e => {
        console.log('Could not open item using any means:', JSON.stringify(trackedProps), e)
        trackException(e, trackedProps, {
            reactComponentPath: 'noMeans',
            message: 'Could not open item using any means',
        })
    })
}

const trackException = (error: any, props: StealthLinkProps, properties?: ICustomProperties) => {
    appInsightsReact.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
        properties: {
            ...properties,
            destination: props.destination,
            id: props.id,
            trackId: props.trackId,
            driveItemId: props.item?.id,
            universalId: props.universalId,
            url: props.url,
        },
    })
}

const trackEvent = (name: string, reactComponentPath: string, props: StealthLinkProps) => {
    appInsightsReact.trackEvent({
        name: name,
        properties: {
            reactComponentPath: reactComponentPath,
            destination: props.destination,
            id: props.id,
            trackId: props.trackId,
            driveItemId: props.item?.id,
            universalId: props.universalId,
            url: props.url,
        },
    })
}

// noinspection JSUnusedGlobalSymbols
export default function StealthLink<E = DriveItem | Message>(props: StealthLinkProps<E>) {
    const axios = useAxios()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const documentsOpen = useDocumentsOpen()

    if (!getId(props)) {
        return <>{props.children}</>
    }

    return (
        <LoadableText isLoading={isLoading}>
            <Link
                as="span"
                disabled={!props.id}
                onClick={() => {
                    setIsLoading(true)
                    stealthClick({ ...props, documentsOpen: props.documentsOpen ?? documentsOpen }, axios).then(
                        () => setIsLoading(false),
                    )
                }}
            >
                {props.children}
            </Link>
        </LoadableText>
    )
}
